"use client";

import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { ClanTagBadge } from "@/components/ui/clan-tag-badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatDate } from "@/lib/format";
import type { ParticipantOverview } from "@/lib/types";
import { ChevronDown, ChevronUp } from "lucide-react";

interface SortConfig {
  key: keyof ParticipantOverview | "clan_name" | "seat_numbers" | "registered_at";
  direction: "asc" | "desc";
}

interface TeilnehmerTableProps {
  participants: ParticipantOverview[];
}

export default function TeilnehmerTable({ participants }: TeilnehmerTableProps) {
  const [query, setQuery] = useState("");
  const [sortConfig, setSortConfig] = useState<SortConfig | null>({
    key: "registered_at",
    direction: "asc",
  });

  const requestSort = (key: SortConfig["key"]) => {
    if (sortConfig?.key === key && sortConfig.direction === "asc") {
      setSortConfig({ key, direction: "desc" });
    } else if (sortConfig?.key === key && sortConfig.direction === "desc") {
      setSortConfig(null);
    } else {
      setSortConfig({ key, direction: "asc" });
    }
  };

  const filteredAndSorted = useMemo(() => {
    let data = [...participants];

    // Filter
    const q = query.trim().toLowerCase();
    if (q) {
      data = data.filter((p) => {
        const haystack = [p.user_name, String(p.registration_id), p.clan_tag ?? "", p.clan_name ?? "", ...p.seat_numbers].join(" ").toLowerCase();
        return haystack.includes(q);
      });
    }

    // Sort
    if (sortConfig) {
      data.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortConfig.key) {
          case "user_id":
            aValue = a.user_id;
            bValue = b.user_id;
            break;
          case "user_name":
            aValue = a.user_name;
            bValue = b.user_name;
            break;
          case "clan_name":
            aValue = a.clan_name ?? "";
            bValue = b.clan_name ?? "";
            break;
          case "seat_numbers":
            aValue = a.seat_numbers.length;
            bValue = b.seat_numbers.length;
            break;
          case "registered_at":
            aValue = new Date(a.registered_at).getTime();
            bValue = new Date(b.registered_at).getTime();
            break;
          default:
            return 0;
        }

        if (typeof aValue === "string" && typeof bValue === "string") {
          const comparison = aValue.localeCompare(bValue, undefined, { sensitivity: "accent" });
          return sortConfig.direction === "asc" ? comparison : -comparison;
        }
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return data;
  }, [participants, query, sortConfig]);

  const getSortIcon = (key: keyof ParticipantOverview | "clan_name" | "seat_numbers" | "registered_at") => {
    if (sortConfig?.key !== key) return <span className="ml-1 h-4 w-4 inline-block" aria-hidden="true" />;
    return sortConfig.direction === "asc" ? <ChevronUp className="ml-1 h-4 w-4" /> : <ChevronDown className="ml-1 h-4 w-4" />;
  };

  return (
    <div className="space-y-3">
      <Input placeholder="Suche nach Name, ID, Sitzplatz, Clan…" value={query} onChange={(e) => setQuery(e.target.value)} className="max-w-sm" />

      {filteredAndSorted.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">{participants.length === 0 ? "Noch keine Anmeldungen." : "Keine Treffer."}</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="cursor-pointer" onClick={() => requestSort("user_id")}>
                <div className="flex items-center">ID {getSortIcon("user_id")}</div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("user_name")}>
                <div className="flex items-center">Name {getSortIcon("user_name")}</div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("clan_name")}>
                <div className="flex items-center">Clan {getSortIcon("clan_name")}</div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("seat_numbers")}>
                <div className="flex items-center">Sitzplatz {getSortIcon("seat_numbers")}</div>
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("registered_at")}>
                <div className="flex items-center">Angemeldet am {getSortIcon("registered_at")}</div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAndSorted.map((p) => (
              <TableRow key={p.user_id}>
                <TableCell className="text-muted-foreground tabular-nums">{p.user_id}</TableCell>
                <TableCell className="font-medium">
                  <span className="inline-flex items-center gap-1.5">
                    {p.user_name}
                    <ClanTagBadge tag={p.clan_tag} />
                  </span>
                </TableCell>
                <TableCell className="text-muted-foreground">{p.clan_name ?? <span className="text-muted-foreground">–</span>}</TableCell>
                <TableCell>{p.seat_numbers.length > 0 ? p.seat_numbers.join(", ") : <span className="text-muted-foreground">–</span>}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{formatDate(p.registered_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
